import { IkeToken } from '@ikegps/core-api';
import * as IkeModel from '@ikegps/core-shared';
import { hasCompleteSpanTypeState } from '@ikegps/core-shared';
import { FieldActionButton } from '../../../components/button/field.action.button';
import { MaterialIcon } from '../../../components/icon/icons';
import { Input } from '../../../components/input/input';
import { VectorInput } from '../../../components/input/vector.input';
import { VectorInputState } from '../../../components/input/vector.input.state';
import { NotificationService } from '../../../components/notification.toast/notification.service';
import { addStyles } from '../../../components/theme';
import { T } from '../../../i18n/index';
import { m, type MithrilVNode } from '../../../mithril';
import { TopologicalEditorCollectionEditCtrl } from '../../../page/topological.editor.page/topological.editor.collection.edit.ctrl';
import { Services } from '../../../services';
import { CurrentState } from '../../../state/page.state';
import { AppStore } from '../../../store';
import { FieldInfo } from '../../field.info/field.info';
import { openSpanLinkModal, type SpanLinkModalResult } from '../../span.link.modal/span.link.modal';
import { TopologicalPointCollectionCache } from '../../topological.editor.map/point.collection.cache';
import type { EditableCollectionController, ICollectionViewController } from '../collection.view.ctrl.interface';
import type { CollectionFieldView } from './field.view';
import { validateSpanLink } from '../../../services/subform.link/span.link.validator';
import { CSS as FieldActionButtonCSS } from '../../../components/button/field.action.button';
import { CollectionModelStateEdit } from '../../../store/collection.edit.model';

const CSS = {
	container: 'c-Vector',
	isLinked: 'c-Vector--linked',
};

addStyles({
	[CSS.isLinked]: {
		display: 'flex',
		'justify-content': 'flex-end',
	},
	[`${CSS.container} .${FieldActionButtonCSS.container}`]: {
		'margin-right': '4px',
	},
});

/**
 * Hybrid check for span vector field.
 * Uses IPoleForm when available (for richer features),
 * falls back to generic SubFormType.SPAN detection for other forms.
 *
 * Returns true only if:
 * 1. The field is inside a subform with SubFormType.SPAN
 * 2. The field is the designated span vector field for that subform
 * 3. The subform has complete span type state
 */
function isSpanVectorField(field: IkeModel.CollectionModelFieldVector, ctrl: EditableCollectionController): boolean {
	if (ctrl.collection == null) {
		return false;
	}

	// First, verify this field is actually inside a SPAN-typed subform
	if (!field.isPartOfSpanSubForm()) {
		return false;
	}

	// Next, verify the span subform has complete type state. This ensures all
	//   required fields are configured as the two way link adapts in the future
	// We know that this will be the parent form as the span subform type can only be applied to a first level subform
	if (!hasCompleteSpanTypeState(field.parent.schema)) {
		return false;
	}

	return true;
}

export interface SpanLinkDecision {
	type: 'two-way' | 'one-way';
	reason?: 'version-mismatch' | 'not-span-context';
}

/**
 * Determines whether to create a two-way or one-way span link.
 * Two-way links require:
 * 1. The field must be in a SPAN subform context
 * 2. Source and target form versions must match
 */
export function determineSpanLinkType(
	field: IkeModel.CollectionModelFieldVector,
	ctrl: EditableCollectionController,
	sourceFormRevision: string,
	targetCollectionModel: IkeModel.CollectionModel
): SpanLinkDecision {
	if (!isSpanVectorField(field, ctrl) || !Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
		return { type: 'one-way', reason: 'not-span-context' };
	}
	if (sourceFormRevision !== targetCollectionModel.schema.revision) {
		return { type: 'one-way', reason: 'version-mismatch' };
	}
	return { type: 'two-way' };
}

async function linkCollection(field: IkeModel.CollectionModelFieldVector, ctrl: EditableCollectionController) {
	if (!ctrl.collection) {
		return;
	}
	const isSpanContext = isSpanVectorField(field, ctrl) && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

	Services.Analytics.trackEvent(
		Services.Analytics.EventCategory.Mutate,
		isSpanContext ? 'span:link:start' : 'vector:link:start',
		field.schema.displayName,
		1
	);
	NotificationService.info({ key: T.COLLECTION_LINK_START });

	const sourceFormRevision = ctrl.collection.schema.revision;

	// Pass context to state BEFORE calling chooseLinkingCollection
	CurrentState.CollectionLink.beginLinking(field.id, {
		formRevision: sourceFormRevision,
		isSpanContext,
	});

	const targetCollection = await ctrl.chooseLinkingCollection(field);
	if (targetCollection == null) {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			isSpanContext ? 'span:link:cancel' : 'vector:link:cancel',
			field.schema.displayName,
			1
		);
		return;
	}

	if (targetCollection.uuid === ctrl.collection.uuid) {
		NotificationService.warn({ key: T.COLLECTION_LINK_INVALID });
		return;
	}

	// Determine link type using summary's formRevision
	const linkDecision = determineSpanLinkType(field, ctrl, sourceFormRevision, targetCollection);

	if (linkDecision.type === 'one-way' && linkDecision.reason === 'version-mismatch') {
		NotificationService.warn({ key: T.SPAN_LINK_VERSION_MISMATCH });
	}

	if (linkDecision.type === 'two-way') {
		const validation = validateSpanLink(ctrl.collection, targetCollection.id, field);

		if (!validation.valid) {
			NotificationService.error({
				key: T.SPAN_LINK_DUPLICATE_ERROR,
				attrs: { existingSpan: validation.existingSpan },
			});

			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Mutate,
				'span:link:duplicate-blocked',
				field.schema.displayName,
				1
			);

			return;
		}

		// Load target collection to display in modal
		const targetCollectionModel = await AppStore.Collection.fetch(targetCollection.revision);
		if (!targetCollectionModel) {
			NotificationService.error({ key: T.ERROR_GENERAL });
			return;
		}

		// Get the source span instance ID (the parent of the vector field)
		const sourceSpanInstanceId = field.parent.instanceId;
		if (!sourceSpanInstanceId || !ctrl.collection) {
			NotificationService.error({ key: T.ERROR_GENERAL });
			return;
		}

		// Check if target has any spans available for selection
		const targetSpans =
			targetCollectionModel.spanSubForm?.value.map((instance) => new IkeModel.SpanSubFormAdaptor(instance)) ?? [];
		const spanLinkingToSource = targetSpans.find((span) => span.spanTargetCollectionId === ctrl.collection.id);
		const availableSpans = spanLinkingToSource
			? [spanLinkingToSource]
			: targetSpans.filter((span) => !span.spanTargetCollectionId);

		let modalResult: SpanLinkModalResult;
		if (availableSpans.length === 0) {
			// No spans to choose from — auto-create without showing modal
			modalResult = { selectedSpanId: null, createNew: true, cancelled: false };
		} else {
			modalResult = await openSpanLinkModal(ctrl.collection, targetCollectionModel, sourceSpanInstanceId);
		}

		if (modalResult.cancelled) {
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Mutate,
				'span:link:modal:cancel',
				field.schema.displayName,
				1
			);
			return;
		}

		const spanSubForm = ctrl.collection.spanSubForm;
		const spanInstance = spanSubForm?.getInstance(sourceSpanInstanceId);

		if (!spanInstance) {
			NotificationService.error({ key: T.ERROR_GENERAL });
			return;
		}

		const span = new IkeModel.SpanSubFormAdaptor(spanInstance);

		// Perform two-way span link
		try {
			if (!modalResult.createNew && !modalResult.selectedSpanId) {
				Services.Analytics.trackEvent(
					Services.Analytics.EventCategory.Mutate,
					'span:link:two-way:error',
					'no-selection',
					1
				);
				NotificationService.error({ key: T.SPAN_LINK_ERROR });
				return;
			}

			// Put target collection into edit mode so mutations go to a proxy state.
			// This allows us to revert the target if Pole A's changes are discarded.
			if (TopologicalEditorCollectionEditCtrl.is(ctrl)) {
				// Topo editor: register as edit controller so sidebar shows "(unsaved)"
				// and the collection view displays the updated spans
				ctrl.registerCollectionForEditing(targetCollectionModel);
			} else if (!targetCollectionModel.isEditing) {
				// Map page: use standalone proxy for rollback.
				targetCollectionModel.beginEdit((state) => new CollectionModelStateEdit(state));
			}

			const result = Services.SubFormLink.performTwoWaySpanLink({
				sourceSpanSubForm: span,
				sourceCollection: ctrl.collection,
				targetCollection: targetCollectionModel,
				targetInstanceId: modalResult.createNew ? undefined : (modalResult.selectedSpanId ?? undefined),
			});

			// Track modified target for save ordering
			CurrentState.CollectionLink.registerModifiedTarget(result.targetCollection, ctrl.collection.id);

			// Invalidate topo cache for both collections so vectors are recomputed with updated positions
			if (TopologicalEditorCollectionEditCtrl.is(ctrl) && ctrl.collectionEditState.collectionRevision) {
				TopologicalPointCollectionCache.dropCollectionVectors(ctrl.collectionEditState.collectionRevision);
				TopologicalPointCollectionCache.dropCollectionVectors(targetCollectionModel.revision);
				ctrl.redrawMap();
			}

			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Mutate,
				'span:link:two-way',
				field.schema.displayName,
				1
			);

			NotificationService.success({ key: T.SPAN_LINK_TWO_WAY_SUCCESS });
			return;
		} catch (_error) {
			// Revert target collection's edit state on failure
			if (TopologicalEditorCollectionEditCtrl.is(ctrl)) {
				ctrl.revertRegisteredCollection(targetCollectionModel.revision);
			} else if (targetCollectionModel.isEditing) {
				targetCollectionModel.finishEdit();
			}

			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Mutate,
				'span:link:two-way:error',
				'unexpected-error',
				1
			);

			NotificationService.error({ key: T.SPAN_LINK_ERROR });
			return;
		}
	}

	// Existing one-way link behavior
	field.Mutator.collection(targetCollection.id);

	if (TopologicalEditorCollectionEditCtrl.is(ctrl) && ctrl.collectionEditState.collectionRevision) {
		TopologicalPointCollectionCache.dropCollectionVectors(ctrl.collectionEditState.collectionRevision);
		ctrl.redrawMap();
	}

	Services.Analytics.trackEvent(
		Services.Analytics.EventCategory.Mutate,
		isSpanContext ? 'span:link:success' : 'vector:link:success',
		field.schema.displayName,
		1
	);
}

export function viewVectorManualEdit(
	field: IkeModel.CollectionModelFieldVector,
	ctrl: EditableCollectionController
): MithrilVNode {
	if (!ctrl.changeState) {
		return null;
	}
	let inputState = ctrl.changeState.getState(field.id) as VectorInputState;

	if (!inputState) {
		const { value } = field;

		inputState = new VectorInputState(field.schema.displayName);
		inputState.onValue(() => {
			field.Mutator.manual(inputState.value);

			// This will ensure topo map is updated (if it exists) and a user is updating vector locations
			TopologicalPointCollectionCache.dropCollectionVectors(field.collection.revision);
		});

		ctrl.changeState.addState(field.id, inputState);

		if (value != null) {
			inputState.setValue({
				distance: value.distance,
				bearing: value.bearing,
				pitch: value.pitch,
			});
		}
	}

	// If none of the inputs are focused, and the field model value changes
	// the input state should also update.
	if (
		field.value !== null &&
		!(
			inputState.distance.meters.focused ||
			inputState.distance.inch.focused ||
			inputState.distance.feet.focused ||
			inputState.bearing.focused
		)
	) {
		inputState.setValue(field.value);
	}
	const onmouseover = () => {
		CurrentState.setHoverId(field.id);
	};
	const onmouseout = () => {
		CurrentState.setHoverId(null);
	};
	const className = Input.CSS.container;

	return m('div', { className, onmouseover, onmouseout }, [VectorInput.view(inputState)]);
}

function viewManualEditButton(field: IkeModel.CollectionModelFieldVector): MithrilVNode {
	return FieldActionButton.view({
		icon: MaterialIcon.EDIT,
		text: T.FIELD_VECTOR_PROVIDER_MANUAL,
		onclick: () => field.Mutator.manual({ bearing: 0, distance: 0, pitch: 0 }),
	});
}

function viewCollectionLinkButton(
	field: IkeModel.CollectionModelFieldVector,
	ctrl: EditableCollectionController
): MithrilVNode {
	let isDisabled = false;

	if (TopologicalEditorCollectionEditCtrl.is(ctrl)) {
		isDisabled = ctrl.isMeasurementActive || ctrl.isRelocatePoleActive;
	}

	const isSpanContext =
		isSpanVectorField(field, ctrl) &&
		hasCompleteSpanTypeState(field.parent.schema) &&
		Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

	return FieldActionButton.view({
		icon: MaterialIcon.SPAN_LINK,
		text: isSpanContext ? T.SPAN_LINK : T.COLLECTION_LINK,
		linkingText: isSpanContext ? T.SPAN_LINK_CANCEL : T.COLLECTION_LINK_CANCEL,
		linking: CurrentState.CollectionLink.isLinking,
		disabled: isDisabled,
		onclick: () => {
			if (CurrentState.CollectionLink.isLinking) {
				CurrentState.CollectionLink.cancelLinking();
				return;
			}

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			linkCollection(field, ctrl);
		},
	});
}

export const VectorManualFieldView: CollectionFieldView<IkeModel.CollectionModelFieldVector> = {
	viewInline(field: IkeModel.CollectionModelFieldVector): string | MithrilVNode {
		if (!field.hasValue()) {
			return null;
		}
		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		const className = Input.CSS.container;
		return m('div', { className, onmouseover, onmouseout }, [FieldInfo.viewVectorInline(field.value)]);
	},

	hasDetail(field: IkeModel.CollectionModelFieldVector) {
		return false;
	},

	viewDetail(field: IkeModel.CollectionModelFieldVector): MithrilVNode {
		return null;
	},

	viewEdit(field: IkeModel.CollectionModelFieldVector, ctrl: ICollectionViewController): MithrilVNode | string {
		// Cast to editable controller - viewEdit is only called when isEditing=true
		const editCtrl = ctrl as EditableCollectionController;
		if (field.hasValue()) {
			return viewVectorManualEdit(field, editCtrl);
		}

		if (
			!(Services.Token.has(IkeToken.FEATURE_COLLECTION_LINK) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE))
		) {
			return viewVectorManualEdit(field, editCtrl);
		}

		const children: MithrilVNode[] = [];

		if (CurrentState.CollectionLink.isLinking) {
			if (CurrentState.CollectionLink.fieldId === field.id) {
				children.push(viewCollectionLinkButton(field, editCtrl));
			}
		} else {
			children.push(viewManualEditButton(field));
			children.push(viewCollectionLinkButton(field, editCtrl));
		}
		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		return m('div', { className: `${CSS.container} ${CSS.isLinked}`, onmouseover, onmouseout }, children);
	},
};
